/**
 * SpawnSessionView Component
 *
 * Dashboard showing spawn session requirements and progress.
 * Displays strains needed, quantities, safety margins, and completion tracking.
 * Part of US-PE-004: Spawn Sessions & Inventory
 */

import { useState, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useSpawnSessions,
  useSpawnSession,
  useSpawnSessionMutations,
  useSpawnDemandSupply
} from '@/hooks/useSpawnSessions'
import { useSpawnMutations } from '@/hooks/useSpawnBatches'
import { supabase } from '@/supabase-client'
import {
  Calendar,
  FlaskConical,
  Package,
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader2,
  ChevronRight,
  RefreshCw
} from 'lucide-react'
import { format } from 'date-fns'

/**
 * Status badge for spawn sessions
 */
/**
 * Derives display status from completion data rather than DB status field.
 * planned → incubating (some done) → done (all done)
 */
function SessionStatusBadge({ completedBags, totalBags }) {
  let config
  if (totalBags > 0 && completedBags >= totalBags) {
    config = { variant: 'default', icon: CheckCircle, label: 'Incubating', className: 'bg-odin-blue' }
  } else if (completedBags > 0) {
    config = { variant: 'default', icon: Clock, label: 'In Progress', className: 'bg-odin-blue' }
  } else {
    config = { variant: 'outline', icon: Clock, label: 'Planned' }
  }

  const Icon = config.icon

  return (
    <Badge variant={config.variant} className={config.className}>
      {Icon && <Icon className="h-3 w-3 mr-1" />}
      {config.label}
    </Badge>
  )
}

/**
 * Main spawn session view component — table layout
 */
export function SpawnSessionView({ sessionId }) {
  const { session, loading, error, refetch } = useSpawnSession(sessionId)
  const { updateItemCompletion } = useSpawnSessionMutations()
  const { createSpawnBatch } = useSpawnMutations()
  const [editingItems, setEditingItems] = useState({})
  const [savingAll, setSavingAll] = useState(false)
  const [creatingBatches, setCreatingBatches] = useState(false)
  const [batchCodes, setBatchCodes] = useState({}) // { batchId: code }

  // Fetch batch codes for linked batches
  useEffect(() => {
    if (!session?.items) return
    const allBatchIds = session.items
      .flatMap(item => item.linked_batch_ids || [])
      .filter(Boolean)
    if (!allBatchIds.length) return

    supabase.functions.invoke('spawn-batches', {
      body: { action: 'list' }
    }).then(({ data }) => {
      const batches = data?.data || data || []
      const codes = {}
      for (const b of (Array.isArray(batches) ? batches : [])) {
        if (allBatchIds.includes(b.id)) {
          codes[b.id] = b.code
        }
      }
      setBatchCodes(codes)
    })
  }, [session?.items])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-6 w-6 animate-spin text-content-secondary" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  if (!session) {
    return null
  }

  const totalBags = session.items?.reduce((sum, item) => sum + item.quantity, 0) || 0
  const completedBags = session.items?.reduce((sum, item) => sum + (item.quantity_completed || 0), 0) || 0

  const getItemEdits = (item) => editingItems[item.id] || {
    completed: item.quantity_completed || 0,
    contaminated: item.quantity_contaminated || 0
  }

  const setItemEdit = (itemId, value) => {
    const num = parseInt(value, 10)
    setEditingItems(prev => ({
      ...prev,
      [itemId]: {
        completed: isNaN(num) ? 0 : Math.max(0, num),
        contaminated: prev[itemId]?.contaminated ?? 0
      }
    }))
  }

  const isItemDirty = (item) => {
    const edits = editingItems[item.id]
    if (!edits) return false
    return edits.completed !== (item.quantity_completed || 0)
  }

  const hasAnyDirty = session.items?.some(item => isItemDirty(item))

  const handleMarkAllComplete = () => {
    const updates = {}
    for (const item of (session.items || [])) {
      updates[item.id] = { completed: item.quantity, contaminated: 0 }
    }
    setEditingItems(updates)
  }

  const handleSaveAll = async () => {
    const dirtyItems = (session.items || []).filter(item => isItemDirty(item))
    if (!dirtyItems.length) return
    setSavingAll(true)
    try {
      for (const item of dirtyItems) {
        const edits = getItemEdits(item)
        await updateItemCompletion(item.id, edits.completed, edits.contaminated)
      }
      setEditingItems({})
      refetch()
    } finally {
      setSavingAll(false)
    }
  }

  // All items completed and no batches created yet
  const allComplete = totalBags > 0 && completedBags >= totalBags
  const batchesAlreadyCreated = session.items?.some(item =>
    item.linked_batch_ids && item.linked_batch_ids.length > 0
  )

  const handleCreateBatches = async () => {
    setCreatingBatches(true)
    try {
      for (const item of (session.items || [])) {
        const completed = item.quantity_completed || 0
        if (completed <= 0) continue

        // Create spawn batch via edge function
        const batch = await createSpawnBatch({
          strain_id: item.strain_id,
          bag_count: completed,
          grain_type: item.grain_type || 'millet',
          bag_weight_kg: item.container_size ? parseFloat(item.container_size) : 1.5,
          inoculation_date: session.scheduled_date,
          notes: `Created from session ${session.code}`
        })

        // Link created batch ID back to session item
        if (batch?.id) {
          await supabase
            .from('spawn_session_items')
            .update({ linked_batch_ids: [batch.id] })
            .eq('id', item.id)
        }
      }
      refetch()
    } catch (err) {
      console.error('[SpawnSessionView] Error creating batches:', err)
    } finally {
      setCreatingBatches(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Header summary */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-semibold text-lg">{session.name || session.code}</span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-sm text-content-secondary">
            <Calendar className="h-4 w-4" />
            {format(new Date(session.scheduled_date), 'EEEE, d MMMM yyyy')}
          </div>
          {session.notes && (
            <p className="text-sm text-content-tertiary mt-1">{session.notes}</p>
          )}
        </div>
        <div className="flex items-center gap-4 text-center">
          <div>
            <div className="text-2xl font-bold">{session.items?.length || 0}</div>
            <div className="text-xs text-content-secondary">Strains</div>
          </div>
          <div>
            <div className="text-2xl font-bold">{totalBags}</div>
            <div className="text-xs text-content-secondary">Total Bags</div>
          </div>
          <div>
            <div className="text-2xl font-bold">{completedBags}</div>
            <div className="text-xs text-content-secondary">Done</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <span className="text-sm text-content-secondary">
          {completedBags} / {totalBags} bags inoculated
        </span>
        <div className="flex items-center gap-2">
          {completedBags < totalBags && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={handleMarkAllComplete}
            >
              <CheckCircle className="h-3.5 w-3.5 mr-1" />
              Mark All Complete
            </Button>
          )}
          {hasAnyDirty && (
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={handleSaveAll}
              disabled={savingAll}
            >
              {savingAll ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
              Save All
            </Button>
          )}
          {allComplete && !batchesAlreadyCreated && !hasAnyDirty && (
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={handleCreateBatches}
              disabled={creatingBatches}
            >
              {creatingBatches ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FlaskConical className="h-3.5 w-3.5 mr-1" />}
              Create Batches
            </Button>
          )}
        </div>
      </div>

      {/* Items table */}
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[180px]">Strain</TableHead>
              <TableHead className="text-center w-[80px]">Format</TableHead>
              <TableHead className="text-center w-[70px]">Planned</TableHead>
              <TableHead className="text-center w-[90px]">Completed</TableHead>
              {batchesAlreadyCreated && <TableHead>Batch Code</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {session.items?.map((item) => {
              const edits = getItemEdits(item)

              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{item.strain?.name}</span>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {item.strain?.code}
                      </Badge>
                    </div>
                    {item.liquid_culture && (
                      <div className="text-xs text-content-tertiary mt-0.5">
                        LC: {item.liquid_culture.culture_code}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-center text-sm text-content-secondary">
                    {item.container_size ? `${item.container_size}kg` : item.container_type || '—'}
                  </TableCell>
                  <TableCell className="text-center font-medium">
                    {item.quantity}
                  </TableCell>
                  <TableCell className="text-center">
                    <Input
                      type="number"
                      min={0}
                      max={item.quantity}
                      value={edits.completed}
                      onChange={(e) => setItemEdit(item.id, e.target.value)}
                      className="w-16 h-8 text-center mx-auto"
                    />
                  </TableCell>
                  {batchesAlreadyCreated && (
                    <TableCell className="font-mono text-sm">
                      {(item.linked_batch_ids || []).map(id => batchCodes[id] || '—').join(', ')}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}

            {/* Totals row */}
            <TableRow className="border-t-2 font-medium">
              <TableCell className="text-sm">Totals</TableCell>
              <TableCell></TableCell>
              <TableCell className="text-center">{totalBags}</TableCell>
              <TableCell className="text-center">{completedBags}</TableCell>
              {batchesAlreadyCreated && <TableCell></TableCell>}
            </TableRow>
          </TableBody>
        </Table>
      </div>

    </div>
  )
}

/**
 * Spawn sessions list component — table layout
 */
export function SpawnSessionsList({ onSelectSession }) {
  const { sessions, loading, error } = useSpawnSessions({ limit: 20 })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-6 w-6 animate-spin text-content-secondary" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  if (sessions.length === 0) {
    return (
      <div className="text-center py-12 text-content-secondary">
        <FlaskConical className="h-8 w-8 mx-auto mb-2 text-content-tertiary" />
        <p>No sessions yet</p>
        <p className="text-sm mt-1">Create one via Add Spawn → Create Session.</p>
      </div>
    )
  }

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Session</TableHead>
            <TableHead>Date</TableHead>
            <TableHead className="text-center">Strains</TableHead>
            <TableHead className="text-center">Bags</TableHead>
            <TableHead className="text-center">Done</TableHead>
            <TableHead className="text-center">Status</TableHead>
            <TableHead className="w-[40px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessions.map((session) => {
            const totalBags = session.items?.reduce((sum, item) => sum + item.quantity, 0) || 0
            const completedBags = session.items?.reduce((sum, item) => sum + (item.quantity_completed || 0), 0) || 0
            const strainCount = session.items?.length || 0

            return (
              <TableRow
                key={session.id}
                className="cursor-pointer hover:bg-surface-secondary"
                onClick={() => onSelectSession?.(session.id)}
              >
                <TableCell className="font-medium">{session.name || session.code}</TableCell>
                <TableCell className="text-sm">
                  {format(new Date(session.scheduled_date), 'd MMM yyyy')}
                </TableCell>
                <TableCell className="text-center">{strainCount}</TableCell>
                <TableCell className="text-center">{totalBags}</TableCell>
                <TableCell className="text-center">
                  {completedBags > 0 ? (
                    <span className={completedBags >= totalBags ? 'text-green-600 font-medium' : ''}>
                      {completedBags}
                    </span>
                  ) : '—'}
                </TableCell>
                <TableCell className="text-center">
                  <SessionStatusBadge completedBags={completedBags} totalBags={totalBags} />
                </TableCell>
                <TableCell>
                  <ChevronRight className="h-4 w-4 text-content-tertiary" />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

/**
 * Spawn demand/supply overview component
 */
export function SpawnDemandSupplyCard() {
  const { demandSupply, loading, error, refetch } = useSpawnDemandSupply()

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-content-secondary" />
        </CardContent>
      </Card>
    )
  }

  const needsAttention = demandSupply.filter(s => s.supply_status === 'needs_session')
  const pending = demandSupply.filter(s => s.supply_status === 'pending_colonisation')
  const sufficient = demandSupply.filter(s => s.supply_status === 'sufficient')

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Spawn Demand vs Supply
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {needsAttention.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 text-odin-red mb-2">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium">Needs Spawn Session</span>
            </div>
            <div className="space-y-2">
              {needsAttention.map((strain) => (
                <div
                  key={strain.strain_id}
                  className="flex items-center justify-between p-2 bg-odin-red/10 rounded border border-odin-red/20"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono text-xs">
                      {strain.strain_code}
                    </Badge>
                    <span className="text-sm">{strain.strain_name}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-odin-red font-medium">Gap: {strain.supply_gap} bags</span>
                    <span className="text-content-secondary ml-2">
                      (Need {strain.bags_needed}, Have {strain.bags_ready})
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {pending.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 text-odin-blue mb-2">
              <Clock className="h-4 w-4" />
              <span className="font-medium">Spawn Colonising</span>
            </div>
            <div className="space-y-2">
              {pending.map((strain) => (
                <div
                  key={strain.strain_id}
                  className="flex items-center justify-between p-2 bg-odin-blue/10 rounded border border-odin-blue/20"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono text-xs">
                      {strain.strain_code}
                    </Badge>
                    <span className="text-sm">{strain.strain_name}</span>
                  </div>
                  <div className="text-sm text-content-secondary">
                    {strain.bags_incubating} incubating, {strain.bags_ready} ready
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {sufficient.length > 0 && (
          <div>
            <div className="flex items-center gap-2 text-odin-green mb-2">
              <CheckCircle className="h-4 w-4" />
              <span className="font-medium">Supply Sufficient</span>
            </div>
            <div className="space-y-2">
              {sufficient.map((strain) => (
                <div
                  key={strain.strain_id}
                  className="flex items-center justify-between p-2 bg-odin-green/10 rounded border border-odin-green/20"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono text-xs">
                      {strain.strain_code}
                    </Badge>
                    <span className="text-sm">{strain.strain_name}</span>
                  </div>
                  <div className="text-sm text-content-secondary">
                    {strain.bags_ready} ready
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {demandSupply.length === 0 && (
          <div className="text-center py-4 text-content-secondary">
            <Package className="h-8 w-8 mx-auto mb-2 text-content-tertiary" />
            <p>No spawn demand data</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default SpawnSessionView
